import { createFileRoute } from "@tanstack/react-router";
import {
  Phone,
  Clock,
  MapPin,
  Sparkles,
  Heart,
  Users,
  Camera,
  ShieldCheck,
  Star,
  Gem,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { OptimizedImage } from "@/components/OptimizedImage";
import { heroImage, logoImage, logoLcpPreload } from "@/lib/images";
import { absoluteUrl } from "@/lib/site";

const PHONE = "415.335.0586";
const PHONE_LINK = "tel:+14153350586";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Blondie's Companionship — San Francisco Bay Area, Open 24/7" },
      {
        name: "description",
        content:
          "Blondie's provides professional, discreet companionship in the San Francisco Bay Area for birthdays, events and dinners. Outcalls, 24/7 availability, fair rates. Call 415.335.0586.",
      },
      { property: "og:title", content: "Elegant Bay Area Companionship for Events & Celebrations" },
      {
        property: "og:description",
        content:
          "Private and group celebrations, dinners and social events across San Francisco and the Bay Area. Discreet, experienced companions — call back in 5–10 minutes.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/") },
      {
        property: "og:image",
        content: absoluteUrl("/blondies-logo.jpg"),
      },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:image",
        content: absoluteUrl("/blondies-logo.jpg"),
      },
    ],
    links: [
      { rel: "canonical", href: absoluteUrl("/") },
      {
        rel: "preload",
        as: "image",
        href: logoLcpPreload.href,
        type: logoLcpPreload.type,
        imageSrcSet: logoLcpPreload.imageSrcSet,
        imageSizes: logoLcpPreload.imageSizes,
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Blondie's",
          url: absoluteUrl("/"),
          description:
            "Professional, discreet companionship for birthdays, events, dinners and special occasions across the San Francisco Bay Area.",
          telephone: "+1-415-335-0586",
          priceRange: "$$",
          areaServed: [
            { "@type": "City", name: "San Francisco", addressRegion: "CA", addressCountry: "US" },
            { "@type": "AdministrativeArea", name: "San Francisco Bay Area" },
          ],
          address: {
            "@type": "PostalAddress",
            addressLocality: "San Francisco",
            addressRegion: "CA",
            addressCountry: "US",
          },
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "reservations",
            telephone: "+1-415-335-0586",
            availableLanguage: "English",
          },
          openingHoursSpecification: {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "00:00",
            closes: "23:59",
          },
        }),
      },
    ],
  }),
  component: Index,
});

const services = [
  {
    icon: Heart,
    title: "Private Celebrations",
    desc: "Intimate, one-on-one company tailored to your evening, your pace and your occasion.",
  },
  {
    icon: Users,
    title: "Group Celebrations",
    desc: "Bring the spark to birthdays, parties and special gatherings with charming company.",
  },
  {
    icon: Camera,
    title: "Modeling & Companionship",
    desc: "Poised, well-presented ladies for events, dinners, dates and social occasions.",
  },
];

const reasons = [
  {
    icon: ShieldCheck,
    title: "Trusted & Discreet",
    desc: "Confidentiality and respect come first — always.",
  },
  {
    icon: Star,
    title: "Experienced",
    desc: "Years of making every occasion feel effortless and memorable.",
  },
  {
    icon: Clock,
    title: "Fast Response",
    desc: "Reach out and we call you back within 5–10 minutes.",
  },
  {
    icon: Gem,
    title: "Reasonable Rates",
    desc: "Premium company at fair, honest pricing — no surprises.",
  },
];

function Index() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground"
      >
        Skip to content
      </a>
      {/* Nav */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-border/40 bg-background/70 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <a href="#top" className="flex items-center gap-2">
            <OptimizedImage
              {...logoImage}
              alt="Blondie's logo"
              width={56}
              height={56}
              sizes="56px"
              className="h-14 w-14 object-contain"
            />
            <span className="font-display text-xl font-semibold tracking-wide text-primary">
              Blondie's
            </span>
          </a>
          <a
            href={PHONE_LINK}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-[var(--shadow-soft)] transition-transform hover:scale-105"
          >
            <Phone className="h-4 w-4" />
            <span className="hidden sm:inline">{PHONE}</span>
            <span className="sm:hidden">Call</span>
          </a>
        </div>
      </header>

      <main id="main">
        {/* Hero */}
        <section
          id="top"
          className="relative flex min-h-screen items-center justify-center overflow-hidden"
        >
          <picture className="absolute inset-0 block h-full w-full">
            {heroImage.sources.map((source) => (
              <source key={source.type} type={source.type} srcSet={source.srcSet} sizes="100vw" />
            ))}
            <img
              src={heroImage.src}
              srcSet={heroImage.srcSet}
              sizes="100vw"
              alt="Elegant pink silk backdrop"
              width={heroImage.width}
              height={heroImage.height}
              className="h-full w-full object-cover"
              decoding="async"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background" />
          <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
            <OptimizedImage
              {...logoImage}
              alt="Blondie's"
              width={220}
              height={220}
              sizes="176px"
              fetchPriority="high"
              className="mx-auto mb-4 h-44 w-44 object-contain drop-shadow-lg"
            />
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/60 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-primary">
              <Sparkles className="h-3.5 w-3.5" /> San Francisco & Bay Area
            </span>
            <h1 className="mt-6 font-display text-5xl font-bold leading-tight text-foreground sm:text-7xl">
              Unforgettable <span className="text-gradient">Company</span> for Every Occasion
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              Elegant companionship for birthdays, events and special occasions. Warm, confident and
              effortlessly charming — whenever you need us.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={PHONE_LINK}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:scale-105"
              >
                <Phone className="h-5 w-5" /> Call {PHONE}
              </a>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground/80">
                <Clock className="h-4 w-4 text-primary" /> Open 24/7 · Outcalls
              </span>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="mx-auto max-w-6xl px-5 py-24">
          <Reveal className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-4xl font-bold sm:text-5xl">What We Offer</h2>
            <p className="mt-4 text-muted-foreground">
              Thoughtful, tasteful company for the moments that matter most.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <article className="group h-full rounded-2xl border border-border bg-card p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-[var(--shadow-soft)]">
                  <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-secondary text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <s.icon className="h-7 w-7" />
                  </div>
                  <h3 className="font-display text-2xl font-semibold">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Why choose */}
        <section className="bg-secondary/40 py-24">
          <div className="mx-auto max-w-6xl px-5">
            <Reveal className="mx-auto max-w-2xl text-center">
              <h2 className="font-display text-4xl font-bold sm:text-5xl">Why Choose Blondie's</h2>
              <p className="mt-4 text-muted-foreground">
                Confidence, comfort and care — the reasons clients keep coming back.
              </p>
            </Reveal>
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {reasons.map((r, i) => (
                <Reveal key={r.title} delay={i * 0.1}>
                  <div className="flex h-full flex-col items-center rounded-2xl bg-card p-7 text-center shadow-sm">
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <r.icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-display text-xl font-semibold">{r.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{r.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Info strip */}
        <section className="mx-auto max-w-6xl px-5 py-24">
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              {
                icon: Clock,
                title: "Open 24/7",
                desc: "Available day or night, whenever the occasion calls.",
              },
              {
                icon: MapPin,
                title: "Entire Bay Area",
                desc: "Serving San Francisco and the wider Bay Area.",
              },
              {
                icon: Sparkles,
                title: "Outcalls",
                desc: "We come to you — discreet and on your schedule.",
              },
            ].map((item, i) => (
              <Reveal key={item.title} delay={i * 0.1}>
                <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-6">
                  <item.icon className="mt-0.5 h-7 w-7 shrink-0 text-primary" />
                  <div>
                    <h3 className="font-display text-xl font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="mx-auto max-w-6xl px-5 py-24">
          <Reveal className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-4xl font-bold sm:text-5xl">Get in Touch</h2>
            <p className="mt-4 text-muted-foreground">
              Prefer to write? Send us a message and we'll get back to you shortly.
            </p>
            <ContactForm />
          </Reveal>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden py-28">
          <div className="absolute inset-0" style={{ background: "var(--gradient-primary)" }} />
          <Reveal className="relative z-10 mx-auto max-w-2xl px-6 text-center">
            <h2 className="font-display text-4xl font-bold text-primary-foreground sm:text-5xl">
              Let's Make It Memorable
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-primary-foreground/85">
              One quick call is all it takes. Reach out and we'll get back to you within 5–10
              minutes.
            </p>
            <a
              href={PHONE_LINK}
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-background px-9 py-4 text-base font-semibold text-primary shadow-xl transition-transform hover:scale-105"
            >
              <Phone className="h-5 w-5" /> Call {PHONE}
            </a>
          </Reveal>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-5 py-10 text-center">
          <OptimizedImage
            {...logoImage}
            alt="Blondie's"
            width={72}
            height={72}
            sizes="72px"
            loading="lazy"
            className="h-[4.5rem] w-[4.5rem] object-contain"
          />
          <p className="font-display text-lg font-semibold text-primary">Blondie's</p>
          <a href={PHONE_LINK} className="text-sm font-medium text-foreground hover:text-primary">
            {PHONE}
          </a>
          <p className="text-sm text-secondary-foreground">
            San Francisco & Bay Area · Open 24/7 · Outcalls
          </p>
          <p className="max-w-md text-sm text-foreground">
            Must be 18 years or older. Blondie's provides companionship and entertainment services
            only.
          </p>
          <p className="text-sm text-foreground">
            © {new Date().getFullYear()} Blondie's. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
